import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, ChefHat, Flame, Activity, Moon, Heart, MessageCircle, HeartPulse, TrendingUp, Zap, Check, CheckCircle2, Users, Clock, Star, Upload, Target, Calculator, Award, Mountain, Bike, Brain, Trophy, Dumbbell, Lightbulb, Calendar, Camera, Droplet, Sun, FlaskConical, Beef, Apple, Salad, Stethoscope, Wheat, Egg, FileText, CircleDot, Scale, Baby } from "lucide-react";
import MarketingNav from "../components/MarketingNav";
import MarketingFooter from "../components/MarketingFooter";
import SEO, { ORG_JSON_LD, WEBSITE_JSON_LD } from "../components/SEO";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_vibrant-wellness-5/artifacts/6upx5on1_Shahid%20white.png";
const FOUNDER_MONTAGE = "https://customer-assets.emergentagent.com/job_vibrant-wellness-5/artifacts/xxwbv7o3_SHAHID%20PROFILE.png";
// Inline <picture> tags below use /hero-people/*.webp + .jpg — kept as small,
// CDN-cached static assets to keep LCP fast (was 7.8 MB of unoptimised JPG/PNG).
const FOUNDER_MOUNTAIN = "https://customer-assets.emergentagent.com/job_vibrant-wellness-5/artifacts/7y8uza1c_Shahid%20mountain%20in%20blue%20top.png";

const features = [
    { key: "calories", title: "Calorie & Deficit Guidance", icon: Calculator, color: "red", copy: "Know exactly how much to eat for your goal — based on your body, activity, and lifestyle. No guesswork." },
    { key: "plans", title: "Adaptive Meal Plans", icon: ChefHat, color: "teal", copy: "7-day plans built around your blood report, allergies, cuisine, and cooking time — and reshaped as your week unfolds." },
    { key: "logging", title: "Instant Meal Understanding", icon: Activity, color: "blue", copy: "Snap a photo or log manually. See if you're on track in real time — no spreadsheets needed." },
    { key: "insights", title: "Personalised Wellness Insights", icon: HeartPulse, color: "red", copy: "Upload your blood report once. Get clear, considered suggestions on what to eat, what to skip, what to fix." },
    { key: "exercise", title: "Adaptive Movement Plans", icon: Dumbbell, color: "red", copy: "22 curated movement plans for home or full gym. Pick, personalise, swap, evolve." },
    { key: "sleep", title: "Sleep-Aware Daily Guidance", icon: Moon, color: "indigo", copy: "Daily sleep logs, score, 14-day rhythm, hygiene checklist, and a 4-4-6-2 guided wind-down ritual." },
    { key: "stress", title: "Stress-Aware Wellness Adjustments", icon: Brain, color: "cyan", copy: "60-second daily check-in plus 4 in-app reset tools: box breathing, 4-7-8, grounding, gratitude journal." },
    { key: "chat", title: "Ask Energize Anytime", icon: MessageCircle, color: "cyan", copy: "Wedding tonight? Late-night cravings? A workout question? Get instant, contextual guidance." },
    { key: "progress", title: "Patterns That Evolve With You", icon: TrendingUp, color: "teal", copy: "Daily rhythm, weekly adherence, calorie consistency. Stay steady without being nagged." },
];

const colorCls = {
    teal: { chip: "bg-teal-50 text-teal-700", dot: "bg-teal-400", text: "text-teal-600" },
    red: { chip: "bg-red-50 text-red-700", dot: "bg-red-400", text: "text-red-500" },
    blue: { chip: "bg-blue-50 text-blue-700", dot: "bg-blue-400", text: "text-blue-500" },
    cyan: { chip: "bg-cyan-50 text-cyan-700", dot: "bg-cyan-400", text: "text-cyan-600" },
    indigo: { chip: "bg-indigo-50 text-indigo-700", dot: "bg-indigo-400", text: "text-indigo-600" },
};

export default function Home() {
    return (
        <div className="bg-[#FAFAF9] min-h-screen">
            <SEO
                title="Personalised Indian Meal Plans for PCOS, Diabetes & Thyroid | Energize.fit"
                description="Energize.fit builds personalised Indian meal plans for PCOS, diabetes, thyroid and hypertension — with 13 micronutrients tracked and plans that adapt daily. Free 7-day trial."
                path="/"
                keywords="indian meal plan, pcos diet plan india, diabetes diet plan india, thyroid diet, hypertension diet, personalised nutrition, adaptive wellness, energize.fit"
                jsonLd={[ORG_JSON_LD, WEBSITE_JSON_LD]}
            />
            <MarketingNav />

            {/* 1. HERO — outcome-first, app-as-product */}
            <section className="pt-32 pb-20 relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        transition={{ duration: 0.8 }}
                        className="md:col-span-7"
                    >
                        <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-stone-500 mb-6">
                            <span className="h-1.5 w-1.5 rounded-full bg-teal-400" /> THE ADAPTIVE WELLNESS PLATFORM
                        </span>
                        <h1 className="font-heading text-5xl md:text-7xl leading-[1.02] tracking-tighter text-stone-900 mb-7">
                            Personalised wellness that <span className="italic text-teal-500">adapts</span> with you.
                        </h1>
                        <p className="text-lg md:text-xl text-stone-600 max-w-xl leading-relaxed mb-3">
                            Built around your goals, health conditions, deficiencies, food preferences and daily habits — and updated every day.
                        </p>
                        <p className="text-sm font-semibold text-stone-900 max-w-xl leading-relaxed mb-10 tracking-tight" data-testid="hero-reason-line">
                            Every recommendation has a reason.
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <Link to="/signup" data-testid="hero-start-app" className="inline-flex items-center gap-2 bg-stone-900 text-white rounded-full px-7 py-4 font-medium hover:-translate-y-1 transition-transform">
                                Start 7-day free trial <ArrowRight className="w-4 h-4" />                            </Link>
                            <Link to="/premium-support" data-testid="hero-explore-premium" className="inline-flex items-center gap-2 bg-white border border-stone-200 text-stone-900 rounded-full px-7 py-4 font-medium hover:bg-stone-50">
                                Explore Premium Support
                            </Link>
                        </div>
                        <p className="text-sm text-stone-500 mt-3">No credit card required · Cancel anytime</p>
                        <div className="flex items-center gap-6 mt-12">
                            <div className="flex">
                                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
                            </div>
                            <p className="text-sm text-stone-500">Cancel anytime. Built by a certified nutritionist & national champion.</p>
                        </div>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.9, delay: 0.2 }}
                        className="md:col-span-5 relative"
                    >
                        <DashboardMockup />
                        {/* Top-left chip — Snap to log (camera input affordance, pulsing). Desktop only. */}
                        <div className="hidden md:flex absolute -top-5 -left-5 bg-white rounded-2xl shadow-xl border border-stone-200 p-4 items-center gap-3 z-10" data-testid="hero-chip-snap">
                            <div className="h-10 w-10 rounded-full bg-teal-500 flex items-center justify-center animate-teal-halo">
                                <Camera className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <p className="text-xs text-stone-500">Log a meal</p>
                                <p className="text-sm font-semibold text-stone-900">Snap a photo →</p>
                            </div>
                        </div>
                        {/* Bottom-left chip — Calorie deficit. Desktop only. */}
                        <div className="hidden md:flex absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl border border-stone-200 p-4 items-center gap-3 z-10">
                            <div className="h-10 w-10 rounded-full bg-teal-400 flex items-center justify-center"><Flame className="w-5 h-5 text-white" /></div>
                            <div>
                                <p className="text-xs text-stone-500">Today's deficit</p>
                                <p className="text-sm font-semibold text-stone-900">-420 kcal ✓</p>
                            </div>
                        </div>
                        {/* Top-right chip — Streak. Desktop only. */}
                        <div className="hidden md:block absolute -top-5 -right-5 bg-white rounded-2xl shadow-xl border border-stone-200 p-4 w-48 z-10">
                            <p className="text-xs text-stone-500 mb-2">12-day streak 🔥</p>
                            <div className="flex gap-1">
                                {[1, 1, 1, 1, 1, 1, 0].map((v, i) => <div key={i} className={`h-2 flex-1 rounded-full ${v ? "bg-teal-400" : "bg-stone-200"}`} />)}
                            </div>
                        </div>
                        {/* Bottom-right chip — Meal plan integration. Desktop only. */}
                        <div className="hidden md:flex absolute -bottom-5 -right-5 bg-white rounded-2xl shadow-xl border border-stone-200 p-4 items-center gap-3 z-10" data-testid="hero-chip-mealplan">
                            <div className="h-10 w-10 rounded-full bg-amber-50 flex items-center justify-center"><Calendar className="w-5 h-5 text-amber-600" /></div>
                            <div>
                                <p className="text-xs text-stone-500">7-day plan</p>
                                <p className="text-sm font-semibold text-stone-900">Day 3 of 7</p>
                            </div>
                        </div>

                        {/* Mobile-only chip strip — 2×2 grid below the mockup, no overlap */}
                        <div className="grid grid-cols-2 gap-2 mt-5 md:hidden">
                            <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-3 flex items-center gap-2.5" data-testid="hero-mobile-chip-snap">
                                <div className="h-8 w-8 rounded-full bg-teal-500 flex items-center justify-center flex-shrink-0">
                                    <Camera className="w-4 h-4 text-white" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] text-stone-500 leading-tight">Log a meal</p>
                                    <p className="text-xs font-semibold text-stone-900 leading-tight">Snap a photo</p>
                                </div>
                            </div>
                            <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-3" data-testid="hero-mobile-chip-streak">
                                <p className="text-[10px] text-stone-500 mb-1.5 leading-tight">12-day streak 🔥</p>
                                <div className="flex gap-1">
                                    {[1, 1, 1, 1, 1, 1, 0].map((v, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${v ? "bg-teal-400" : "bg-stone-200"}`} />)}
                                </div>
                            </div>
                            <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-3 flex items-center gap-2.5">
                                <div className="h-8 w-8 rounded-full bg-teal-400 flex items-center justify-center flex-shrink-0"><Flame className="w-4 h-4 text-white" /></div>
                                <div className="min-w-0">
                                    <p className="text-[10px] text-stone-500 leading-tight">Today's deficit</p>
                                    <p className="text-xs font-semibold text-stone-900 leading-tight">-420 kcal ✓</p>
                                </div>
                            </div>
                            <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-3 flex items-center gap-2.5">
                                <div className="h-8 w-8 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0"><Calendar className="w-4 h-4 text-amber-600" /></div>
                                <div className="min-w-0">
                                    <p className="text-[10px] text-stone-500 leading-tight">7-day plan</p>
                                    <p className="text-xs font-semibold text-stone-900 leading-tight">Day 3 of 7</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* 1b. BUILT AROUND YOU — proof of personalisation via an interactive
                condition picker. Each card reveals what the plan tracks for that
                condition and routes the visitor to the matching landing page. */}
            <section className="py-24 bg-white border-y border-stone-200" data-testid="home-built-around-you">
                <div className="max-w-6xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5">
                        <p className="text-xs font-bold tracking-[0.2em] uppercase text-teal-700 mb-3" data-testid="built-around-eyebrow">Your condition. Your cuisine. Your plan.</p>
                        <h2 className="font-heading text-4xl md:text-5xl tracking-tight text-stone-900 leading-[1.05] mb-5">
                            Built for the way Indians actually eat — and what your body specifically needs.
                        </h2>
                        <p className="text-lg text-stone-600 leading-relaxed mb-7 max-w-md">
                            Not a generic wellness app. A plan calibrated for your condition, your regional cuisine, and your real kitchen — and updated every day based on what you actually ate.
                        </p>
                        <ul className="space-y-3">
                            {[
                                { label: "Conditions.", body: "PCOS, diabetes, thyroid, hypertension + 24 more — each condition gets a different plate, not the same low-carb toggle." },
                                { label: "13 micronutrients tracked.", body: "Iron, B12, Vitamin D, folate, zinc, omega-3 — scored per dish using ICMR-NIN data, not estimated by AI." },
                                { label: "Your cuisine.", body: "North Indian, South Indian, Bengali, Gujarati, Rajasthani — or a mix. The plan speaks your kitchen's language." },
                                { label: "Your kitchen.", body: "Cooking time, equipment, household size — the plan is built around your real life, not an ideal one." },
                                { label: "Your labs.", body: "Upload your blood report — every marker tracked over time, compared against your previous report, with an AI-narrated progress story." },
                            ].map((row) => (
                                <li key={row.label} className="flex items-start gap-3">
                                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-teal-600 shrink-0" />
                                    <div>
                                        <span className="text-sm font-semibold" style={{ color: "#0F6E56" }}>{row.label}</span>{" "}
                                        <span className="text-sm text-stone-600">{row.body}</span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="lg:col-span-7 lg:sticky lg:top-24">
                        <ConditionPicker />
                    </div>
                </div>
            </section>

            {/* 1c. HOW IT WORKS — 3 simple steps. Sequenced before the "math"
                section so the user understands the inputs/loop BEFORE they see
                the calorie target it produces. */}
            <section className="py-24" data-testid="home-how-it-works">
                <div className="max-w-6xl mx-auto px-6 md:px-10">
                    <div className="text-center mb-16">
                        <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-500 mb-3">HOW IT WORKS</p>
                        <h2 className="font-heading text-4xl md:text-5xl tracking-tight text-stone-900">From signup to on-track in minutes.</h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        <Step n="01" title="Tell us about yourself." body="A short, considered onboarding. Goals, conditions, blood report, preferences, lifestyle." color="teal" icon={Upload} />
                        <Step n="02" title="Get a personalised plan." body="Meals, movement, sleep and stress guidance — every recommendation tied to something you told us." color="red" icon={Sparkles} />
                        <Step n="03" title="It adapts with you." body="Log a meal, sleep well, feel stressed — your day quietly reshapes around the signals it sees." color="cyan" icon={TrendingUp} />
                    </div>
                </div>
            </section>

            {/* 1d. THEN WE DO THE MATH — the bridge between onboarding inputs
                and the daily reasons cards. Shows the actual numbers we compute
                for each user: kcal/day, deficit, timeline, macros. The "AI is
                doing real work" proof point, positioned right after How It Works
                so the user just saw the inputs that produced these numbers. */}
            <section className="py-24" data-testid="home-targets">
                <div className="max-w-6xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5 lg:order-2">
                        <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-500 mb-3">THE NUMBERS BEHIND THE PLAN</p>
                        <h2 className="font-heading text-4xl md:text-5xl tracking-tight text-stone-900 leading-[1.05] mb-5">
                            Then we do the <span className="italic text-teal-500">math</span>.
                        </h2>
                        <p className="text-lg text-stone-600 leading-relaxed mb-4">
                            Once we know you, we calculate exactly what your body needs.
                        </p>
                        <p className="text-stone-600 leading-relaxed max-w-md mb-7">
                            Calories. Macros. Deficit. Timeline. No generic numbers, no copy-paste templates — every value is computed from your stats, goals and signals. And every recommendation that follows comes with the reason behind it.
                        </p>
                        <ul className="space-y-2.5">
                            {[
                                "BMR + activity → TDEE",
                                "Goal-aware safe deficit (300–500 kcal)",
                                "Protein scaled to your body weight",
                                "Recalculated every week as your weight shifts",
                            ].map((line) => (
                                <li key={line} className="flex items-start gap-2.5">
                                    <Check className="w-4 h-4 text-teal-500 mt-0.5 shrink-0" />
                                    <span className="text-sm text-stone-700">{line}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="lg:col-span-7 lg:order-1">
                        <TargetsMockup />
                    </div>
                </div>
            </section>

            {/* 3. EVERY RECOMMENDATION HAS A REASON — proves the intelligence layer.
                Two cards side-by-side: (a) blood-report-driven nudge, (b) real-time
                course-correct nudge. Replaces the prior "Wellness that works together"
                feature grid (collapsed because it was a checklist, not a value prop). */}
            <section className="py-24 bg-white border-y border-stone-200" data-testid="home-recommendation-reason">
                <div className="max-w-6xl mx-auto px-6 md:px-10">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                        <div className="max-w-3xl">
                            <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-500 mb-3">THE INTELLIGENCE LAYER</p>
                            <h2 className="font-heading text-4xl md:text-5xl tracking-tight text-stone-900 leading-[1.05] mb-5">
                                Every recommendation has a <span className="italic text-teal-500">reason</span>.
                            </h2>
                            <p className="text-lg text-stone-600 leading-relaxed">
                                Not a random suggestion. Not a generic nudge. Every meal, every swap, every food we ask you to add is tied to a specific signal — your blood report, your conditions, your day so far.
                            </p>
                        </div>
                        <Link to="/platform" data-testid="reason-see-all-features" className="group inline-flex items-center gap-2 text-stone-900 hover:text-teal-700 font-medium underline underline-offset-8 decoration-teal-400 decoration-2 transition-colors whitespace-nowrap">
                            See how it all works <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </Link>
                    </div>
                    <div className="grid lg:grid-cols-2 gap-6">
                        <DeficiencyReasonCard />
                        <CourseCorrectReasonCard />
                    </div>
                </div>
            </section>

            {/* 4. FOUR PILLARS — compact strip. Demoted from above-the-fold because
                pillars are a feature checklist, not the primary value proposition.
                Sits here after we've earned trust via Built Around You + How It Works
                + Every Recommendation Has a Reason. */}
            <section className="py-20" data-testid="home-pillars-strip">
                <div className="max-w-6xl mx-auto px-6 md:px-10">
                    <div className="text-center mb-10">
                        <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-stone-500 mb-2">HOW IT ALL CONNECTS</p>
                        <h2 className="font-heading text-3xl md:text-4xl tracking-tight text-stone-900">One platform. Four pillars.</h2>
                        <p className="text-stone-600 mt-3 max-w-xl mx-auto text-sm md:text-base">Nutrition, movement, sleep and stress — each tracked, each guided, each tuned to your goals and signals.</p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                        <PillarStrip imgKey="nutrition" label="Nutrition" tone="emerald" tagline="Meals that adapt." />
                        <PillarStrip imgKey="movement" label="Movement" tone="coral" tagline="Plans for body + space." />
                        <PillarStrip imgKey="sleep" label="Sleep" tone="indigo" tagline="Rhythm, not rigidity." />
                        <PillarStrip imgKey="stress" label="Stress" tone="cyan" tagline="Resets, when you need." />
                    </div>
                </div>
            </section>

            {/* 5. TESTIMONIALS — early reviews. 2-up grid so Andrea's
                Course-Correct quote (the flagship wow moment) gets equal
                billing alongside Priya's PCOS-clarity quote. More cards land
                here as real reviews come in from paid users. */}
            <section className="py-20 bg-stone-900 text-white relative overflow-hidden" data-testid="home-testimonial">
                <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(45,212,191,.7), transparent 40%), radial-gradient(circle at 80% 80%, rgba(255,255,255,.4), transparent 40%)" }} />
                <div className="max-w-6xl mx-auto px-6 md:px-10 relative">
                    <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-teal-300 mb-12 text-center">EARLY USER REVIEWS</p>
                    <div className="grid md:grid-cols-2 gap-8 md:gap-10" data-testid="home-testimonial-grid">
                        {/* Card 1 — Andrea: Course Correct + blood report proof */}
                        <figure className="bg-stone-800/50 backdrop-blur-sm border border-stone-700/60 rounded-3xl p-8 md:p-10 flex flex-col" data-testid="testimonial-andrea">
                            <svg className="w-8 h-8 text-teal-400/70 mb-5" fill="currentColor" viewBox="0 0 32 32" aria-hidden="true">
                                <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                            </svg>
                            <blockquote className="font-heading text-lg md:text-xl leading-[1.45] text-white mb-6 flex-1">
                                &ldquo;I&rsquo;d tried different diet apps before this. Most gave me the same generic plan. Energize.fit was the first one that actually used my blood report and conditions. I really liked that when I skipped lunch one day, by 4 PM the app had already rebalanced my evening to make up the protein gap.&rdquo;
                            </blockquote>
                            <figcaption className="flex flex-col gap-1 pt-5 border-t border-stone-700/60">
                                <p className="font-semibold text-white">Mrs. Andrea Shankar</p>
                                <p className="text-[11px] text-stone-400 uppercase tracking-[0.15em]">Energize.fit member</p>
                            </figcaption>
                        </figure>

                        {/* Card 2 — Priya: PCOS clarity */}
                        <figure className="bg-stone-800/50 backdrop-blur-sm border border-stone-700/60 rounded-3xl p-8 md:p-10 flex flex-col" data-testid="testimonial-priya">
                            <svg className="w-8 h-8 text-teal-400/70 mb-5" fill="currentColor" viewBox="0 0 32 32" aria-hidden="true">
                                <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                            </svg>
                            <blockquote className="font-heading text-lg md:text-xl leading-[1.45] text-white mb-6 flex-1">
                                &ldquo;It&rsquo;s the first time an app actually understood my PCOS. The meals are low-glycemic without me having to ask, and every dish tells me what nutrient it&rsquo;s helping me fix. I stopped second-guessing what to eat — and the grocery list lands every week, so my Sundays aren&rsquo;t a panic anymore.&rdquo;
                            </blockquote>
                            <figcaption className="flex flex-col gap-1 pt-5 border-t border-stone-700/60">
                                <p className="font-semibold text-white">Priya Anand</p>
                                <p className="text-[11px] text-stone-400 uppercase tracking-[0.15em]">Core+ subscriber · Bengaluru</p>
                            </figcaption>
                        </figure>
                    </div>
                </div>
            </section>

            {/* 5a. FOUNDER TRUST — sequenced right after testimonials so the
                visitor reads peer proof, then meets the person behind it, before
                ever seeing a price. Trust precedes the ask. */}
            <section className="py-24">
                <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-5">
                        <div className="grid grid-cols-2 gap-4">
                            <picture>
                                <source type="image/webp" srcSet="/hero-people/shahid_blue_640.webp 640w, /hero-people/shahid_blue_1080.webp 1080w" sizes="(min-width: 768px) 220px, 50vw" />
                                <img src="/hero-people/shahid_blue_1080.jpg" width="540" height="720" alt="Shahid Khan, founder of Energize.fit" loading="lazy" decoding="async" className="rounded-2xl aspect-[3/4] object-cover w-full shadow-xl shadow-stone-900/10" />
                            </picture>
                            <picture>
                                <source type="image/webp" srcSet="/hero-people/shahid_peak_640.webp 640w, /hero-people/shahid_peak_1080.webp 1080w" sizes="(min-width: 768px) 220px, 50vw" />
                                <img src="/hero-people/shahid_peak_1080.jpg" width="540" height="720" alt="Shahid at Friendship Peak" loading="lazy" decoding="async" className="rounded-2xl aspect-[3/4] object-cover w-full shadow-xl shadow-stone-900/10 mt-10" />
                            </picture>
                        </div>
                    </div>
                    <div className="md:col-span-7">
                        <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-500 mb-4">MEET THE FOUNDER</p>
                        <h2 className="font-heading text-4xl md:text-5xl tracking-tighter text-stone-900 mb-5 leading-[1.05]">Built by a nutrition professional who believes wellness advice should fit real life.</h2>
                        <p className="text-stone-600 text-lg leading-relaxed mb-5">
                            <strong className="text-stone-900">Shahid Khan</strong> is the founder of Energize.fit and holds a <strong className="text-teal-700">Post Graduate Diploma in Nutrition and Dietetics</strong>. He built the platform to make personalised guidance around nutrition, movement, sleep, and stress more practical, accessible, and easier to follow in everyday life.
                        </p>
                        <p className="text-stone-600 leading-relaxed mb-8">
                            His background combines formal nutrition training with years of experience in fitness, endurance, and outdoor sport — from roller skating and cycling to rally driving and mountain expeditions. That mix of education and lived experience shaped Energize.fit into a system built for real people, real routines, and real goals.
                        </p>
                        <div className="grid grid-cols-3 gap-4 mb-8">
                            <Cred icon={Award} label="Post Graduate Diploma" sub="Nutrition & Dietetics" color="teal" />
                            <Cred icon={Trophy} label="National Champion" sub="Roller Skating" color="red" />
                            <Cred icon={Mountain} label="Endurance Sports" sub="Cycling, Rallying, Mountaineering, CrossFit" color="blue" />
                        </div>
                        <Link to="/about" data-testid="founder-know-more" className="group inline-flex items-center gap-2 text-stone-900 hover:text-teal-700 font-medium underline underline-offset-8 decoration-teal-400 decoration-2 transition-colors">
                            Read Shahid&rsquo;s full story <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* 5b. APP PRICING ANCHOR STRIP — appears after the founder section
                so the visitor has built trust before being asked to pay. Anchors
                ₹299 Core before the Premium upgrade block below. */}
            <section className="py-16 bg-gradient-to-br from-teal-50 via-white to-cyan-50/40 border-y border-stone-200" data-testid="home-pricing-strip">
                <div className="max-w-5xl mx-auto px-6 md:px-10">
                    <div className="grid md:grid-cols-12 gap-8 items-center">
                        <div className="md:col-span-7">
                            <p className="text-xs font-bold tracking-[0.2em] uppercase text-teal-700 mb-3">READY WHEN YOU ARE</p>
                            <h2 className="font-heading text-3xl md:text-4xl tracking-tight text-stone-900 leading-[1.1] mb-4">
                                Start free for 7 days.<br />
                                <span className="text-teal-600">Then ₹299/mo. Cancel anytime.</span>
                            </h2>
                            <p className="text-stone-600 leading-relaxed max-w-lg mb-4">
                                One subscription unlocks every pillar — nutrition, movement, sleep, stress. No add-ons, no surprises. Upgrade to Core+ at ₹399/mo when you want longer plans, custom movement plans, and 3× more AI.
                            </p>
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-teal-800 bg-teal-100/70 border border-teal-200 rounded-full px-3 py-1.5" data-testid="pricing-strip-trust-badge">
                                <Sparkles className="w-3 h-3" /> First week free · No credit card · then ₹299
                            </span>
                        </div>
                        <div className="md:col-span-5">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="bg-white border-2 border-stone-200 rounded-2xl p-5 text-center">
                                    <p className="text-[11px] font-bold tracking-wider uppercase text-stone-500 mb-2">Core</p>
                                    <p className="font-heading text-3xl text-stone-900">₹299</p>
                                    <p className="text-[11px] text-stone-500 mt-1">/month</p>
                                </div>
                                <div className="bg-stone-900 text-white rounded-2xl p-5 text-center relative ring-2 ring-teal-400/40">
                                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-teal-400 text-stone-950 text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full">Popular</span>
                                    <p className="text-[11px] font-bold tracking-wider uppercase text-stone-400 mb-2">Core+</p>
                                    <p className="font-heading text-3xl">₹399</p>
                                    <p className="text-[11px] text-stone-400 mt-1">/month</p>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-3 mt-5">
                                <Link to="/signup" data-testid="pricing-strip-trial" className="flex-1 inline-flex items-center justify-center gap-2 bg-stone-900 text-white rounded-full px-5 py-3 text-sm font-medium hover:-translate-y-0.5 transition-transform">
                                    Start free trial <ArrowRight className="w-4 h-4" />
                                </Link>
                                <Link to="/pricing" data-testid="pricing-strip-see-all" className="flex-1 inline-flex items-center justify-center gap-2 bg-white border border-stone-200 text-stone-900 rounded-full px-5 py-3 text-sm font-medium hover:bg-stone-50">
                                    See full pricing
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 6. PREMIUM UPGRADE — moved here so app pricing (₹299) is anchored first */}
            <section className="py-24 bg-stone-900 text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-20 pointer-events-none">
                    <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-red-400 blur-3xl" />
                    <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full bg-cyan-400 blur-3xl" />
                </div>
                <div className="max-w-6xl mx-auto px-6 md:px-10 relative">
                    <div className="grid md:grid-cols-12 gap-10 items-center">
                        <div className="md:col-span-7">
                            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-red-300 bg-red-500/20 rounded-full px-4 py-1.5 mb-5">
                                <Users className="w-3 h-3" /> OPTIONAL UPGRADE
                            </span>
                            <h2 className="font-heading text-4xl md:text-6xl tracking-tighter leading-[1.05] mb-6">
                                Want a real human in your corner?
                            </h2>
                            <p className="text-stone-300 text-lg leading-relaxed max-w-xl mb-8">
                                Most people do well with the app alone. For more complex situations like diabetes, thyroid issues, PCOS, and heart health, you can upgrade to direct support from a certified nutritionist.
                            </p>
                            <ul className="space-y-3 mb-10">
                                {[
                                    "Customized meal plans crafted and reviewed by an expert",
                                    "Direct WhatsApp access to your coach",
                                    "Weekly accountability when you need it most",
                                    "Faster decision support when life gets tricky",
                                    "Full Energize.fit Core+ app access included",
                                ].map((b, i) => (
                                    <li key={i} className="flex items-start gap-3 text-stone-200"><Check className="w-4 h-4 text-teal-400 mt-1 flex-shrink-0" /> <span>{b}</span></li>
                                ))}
                            </ul>
                            <div className="flex flex-wrap gap-3">
                                <Link to="/premium-support" data-testid="premium-explore" className="inline-flex items-center gap-2 bg-white text-stone-900 rounded-full px-7 py-4 font-medium hover:-translate-y-0.5 transition-transform">
                                    Explore Premium Support <ArrowRight className="w-4 h-4" />
                                </Link>
                                <Link to="/contact" data-testid="premium-talk-coach" className="inline-flex items-center gap-2 border border-white/30 text-white rounded-full px-7 py-4 font-medium hover:bg-white/10">
                                    Talk to an expert
                                </Link>
                            </div>
                        </div>
                        <div className="md:col-span-5">
                            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-6 space-y-4">
                                <div className="flex items-center gap-3 pb-4 border-b border-white/10">
                                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-teal-400 to-cyan-400 flex items-center justify-center font-heading text-lg text-stone-900">SK</div>
                                    <div>
                                        <p className="font-medium">Shahid Khan & team</p>
                                        <p className="text-xs text-stone-400">Post Graduate Diploma in Nutrition & Dietetics</p>
                                    </div>
                                </div>
                                <div className="space-y-3 text-sm">
                                    <PremiumTier name="Kickstart Wellness" period="1 month" price="₹4,999" />
                                    <PremiumTier name="Health Transformation" period="3 months" price="₹12,999" featured />
                                    <PremiumTier name="Complete Transformation" period="6 months" price="₹21,999" />
                                </div>
                                <p className="text-[11px] text-stone-500 pt-2 border-t border-white/10">Optional add-on. The Energize.fit app at ₹299/mo is the core product.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 7. WHY ENERGIZE.FIT */}
            <section className="py-24 bg-white border-t border-stone-200">
                <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
                    <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-500 mb-4">WHY ENERGIZE.FIT</p>
                    <h2 className="font-heading text-4xl md:text-5xl tracking-tighter text-stone-900 mb-6 leading-[1.1]">
                        Energy is the foundation of a better life.
                    </h2>
                    <p className="text-lg text-stone-600 leading-relaxed">
                        When nutrition, movement, sleep, and stress are aligned, people feel better, perform better, and live better. Energize.fit was built to make that guidance practical and personal — through one adaptive platform, with expert support available when you need deeper customisation.
                    </p>
                </div>
            </section>

            {/* 8. FINAL CTA */}
            <section className="py-24 pb-32">
                <div className="max-w-6xl mx-auto px-6 md:px-10">
                    <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-br from-teal-400 via-cyan-400 to-blue-400 p-12 md:p-16 text-stone-900">
                        <div className="max-w-2xl">
                            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] bg-stone-900 text-white rounded-full px-3 py-1 mb-5">7-day free trial • Core ₹299/mo • Core+ ₹399/mo</span>
                            <h3 className="font-heading text-4xl md:text-6xl mb-5 tracking-tighter leading-[1.05]">Take the first step today.</h3>
                            <p className="text-lg mb-8 text-stone-800/90">Sign up and start your 7-day free trial. Personalized guidance in minutes. Premium human support is just a tap away when you need it.</p>
                            <div className="flex flex-wrap gap-3">
                                <Link to="/signup" data-testid="cta-signup" className="inline-flex items-center gap-2 bg-stone-900 text-white rounded-full px-7 py-4 font-medium hover:-translate-y-1 transition-transform">
                                    Start with the app <ArrowRight className="w-4 h-4" />
                                </Link>
                                <Link to="/pricing" data-testid="cta-see-plans" className="inline-flex items-center gap-2 bg-white/90 text-stone-900 rounded-full px-7 py-4 font-medium hover:bg-white">
                                    See plans
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <MarketingFooter />
        </div>
    );
}

function Step({ n, title, body, color, icon: Icon }) {
    const c = colorCls[color];
    return (
        <div>
            <div className={`inline-flex items-center gap-2 ${c.chip} rounded-full px-3 py-1 mb-5`}>
                <Icon className={`w-3.5 h-3.5 ${c.text}`} />
                <span className="text-xs font-bold tracking-wider">{n}</span>
            </div>
            <h3 className="font-heading text-2xl text-stone-900 mb-3">{title}</h3>
            <p className="text-stone-600 leading-relaxed">{body}</p>
        </div>
    );
}

function PillarCard({ title, status, body, color, icon: Icon }) {
    const c = colorCls[color];
    const live = status === "Live";
    return (
        <div className={`rounded-2xl p-6 border ${live ? "bg-white border-stone-200" : "bg-stone-50 border-stone-200"}`}>
            <Icon className={`w-5 h-5 ${c.text} mb-3`} strokeWidth={1.5} />
            <div className="flex items-center gap-2 mb-2 flex-wrap">
                <h4 className="font-heading text-xl text-stone-900">{title}</h4>
                <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${live ? "bg-teal-500 text-white" : "bg-stone-300 text-stone-700"}`}>{status}</span>
            </div>
            <p className="text-sm text-stone-600 leading-relaxed">{body}</p>
        </div>
    );
}

function PillarStrip({ imgKey, label, tone, tagline }) {
    // Compact pillar tile shown on Home directly under the hero.
    // Icon = quadrant sliced from the brand emblem so colours/style stay 100% on-brand.
    const toneCls = {
        emerald: { bg: "bg-emerald-50/70", border: "border-emerald-200/70", ring: "ring-emerald-100" },
        coral: { bg: "bg-rose-50/70", border: "border-rose-200/70", ring: "ring-rose-100" },
        indigo: { bg: "bg-indigo-50/70", border: "border-indigo-200/70", ring: "ring-indigo-100" },
        cyan: { bg: "bg-cyan-50/70", border: "border-cyan-200/70", ring: "ring-cyan-100" },
    }[tone] || { bg: "bg-stone-50", border: "border-stone-200", ring: "ring-stone-100" };
    return (
        <div className={`rounded-2xl border ${toneCls.border} ${toneCls.bg} p-4 md:p-5 flex items-center gap-3 md:gap-4`} data-testid={`home-pillar-${label.toLowerCase()}`}>
            <div className={`h-12 w-12 md:h-14 md:w-14 rounded-2xl bg-white flex items-center justify-center shrink-0 shadow-sm ring-1 ${toneCls.ring}`}>
                <picture>
                    <source type="image/webp" srcSet={`/pillars/${imgKey}.webp`} />
                    <img src={`/pillars/${imgKey}.png`} alt={`${label} pillar`} className="h-9 w-9 md:h-11 md:w-11 object-contain" loading="lazy" width="44" height="44" />
                </picture>
            </div>
            <div className="min-w-0">
                <p className="font-heading text-base md:text-lg text-stone-900 leading-tight">{label}</p>
                <p className="text-[11px] md:text-xs text-stone-600 leading-tight mt-0.5">{tagline}</p>
            </div>
        </div>
    );
}


function PremiumTier({ name, period, price, strike, featured }) {
    return (
        <div className={`flex items-center justify-between rounded-xl px-4 py-3 ${featured ? "bg-white/10 ring-1 ring-teal-400/50" : "bg-white/5"}`}>
            <div>
                <p className="font-medium">{name}</p>
                <p className="text-xs text-stone-400">{period}</p>
            </div>
            <div className="text-right">
                <p className="font-heading text-xl">{price}</p>
                {strike && <p className="text-xs text-stone-400 line-through">{strike}</p>}
            </div>
        </div>
    );
}

function Cred({ icon: Icon, label, sub, color }) {
    const c = colorCls[color];
    return (
        <div className="bg-white border border-stone-200 rounded-xl p-4">
            <Icon className={`w-5 h-5 ${c.text} mb-2`} strokeWidth={1.5} />
            <p className="font-heading text-base text-stone-900 leading-tight">{label}</p>
            <p className="text-xs text-stone-500 mt-1.5 leading-snug">{sub}</p>
        </div>
    );
}

function DashboardMockup() {
    return (
        <div className="relative bg-gradient-to-br from-stone-50 via-white to-teal-50/30 rounded-[2rem] p-3 sm:p-5 shadow-2xl shadow-stone-900/10 border border-stone-200">
            <div className="bg-white rounded-2xl border border-stone-200 p-3 sm:p-5 flex flex-col gap-3 sm:gap-3.5">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-[10px] uppercase tracking-wider text-stone-400">Today</p>
                        <p className="font-heading text-lg text-stone-900">Good morning, Aarav</p>
                    </div>
                    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-teal-400 to-cyan-400" />
                </div>

                {/* Calorie ring + macros (protein bar in red flags the gap) */}
                <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-3.5 flex items-center gap-4">
                    <div className="relative w-20 h-20 flex-shrink-0">
                        <svg viewBox="0 0 80 80" className="-rotate-90">
                            <circle cx="40" cy="40" r="32" stroke="#e7e5e4" strokeWidth="6" fill="none" />
                            <circle cx="40" cy="40" r="32" stroke="#14b8a6" strokeWidth="6" fill="none" strokeLinecap="round" strokeDasharray="160 200" />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <p className="font-heading text-base text-stone-900">1,420</p>
                            <p className="text-[8px] text-stone-500">/ 1,800</p>
                        </div>
                    </div>
                    <div className="flex-1 space-y-1.5 text-[11px]">
                        <MacroBar label="Protein" pct={70} color="bg-red-400" value="-28g" />
                        <MacroBar label="Carbs" pct={75} color="bg-amber-400" value="✓" />
                        <MacroBar label="Fat" pct={70} color="bg-blue-400" value="✓" />
                    </div>
                </div>

                {/* 4 pillars — tiny tiles. Nutrition is hero (in macro ring above), so we show breadth via Movement/Sleep/Stress here */}
                <div className="grid grid-cols-3 gap-1.5 text-[9px]">
                    <PillarTile icon={Dumbbell} label="Movement" value="4 / 6" tone="red" />
                    <PillarTile icon={Moon} label="Sleep" value="7.5h" tone="indigo" />
                    <PillarTile icon={Brain} label="Stress" value="Balanced" tone="cyan" />
                </div>

                {/* Today's meals timeline — the LOOP in action */}
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Today's meals</p>
                        <p className="text-[9px] text-stone-400">3 of 4 meals logged</p>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        <MealCard
                            label="Breakfast"
                            time="8:30am"
                            kcal="290 cal"
                            name="Idli with Veggies"
                            img="/hero-meals/breakfast-idli.jpg"
                            fallback="🍚"
                            logged
                        />
                        <MealCard
                            label="Lunch"
                            time="1:15pm"
                            kcal="540 cal"
                            name="Roti, Veggies & Curd"
                            img="/hero-meals/lunch-roti-thali.jpg"
                            fallback="🫓"
                            logged
                        />
                        <MealCard
                            label="Dinner"
                            time="8:00pm"
                            kcal="480 cal"
                            name="Rice & Chickpeas Bowl"
                            img="/hero-meals/dinner-rice-chickpeas.jpg"
                            fallback="🍛"
                            logged
                        />
                    </div>
                </div>

                {/* Course Correct nudge — the unique-in-India differentiator (boosted prominence) */}
                <div className="bg-gradient-to-br from-amber-100 via-amber-50 to-orange-100 border-2 border-amber-300 rounded-xl p-3 shadow-sm" data-testid="hero-course-correct">
                    <div className="flex items-start gap-2.5">
                        <img
                            src="/hero-meals/suggestion-grilled-paneer.jpg"
                            alt="Grilled paneer"
                            className="w-14 h-14 rounded-lg object-cover flex-shrink-0 ring-2 ring-amber-400 animate-paneer-bounce"
                            loading="lazy"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-0.5">
                                <p className="text-[11px] font-bold uppercase tracking-wider text-amber-900 flex items-center gap-1">
                                    <Lightbulb className="w-3 h-3" /> Course correct
                                </p>
                                <span className="text-[9px] font-medium text-stone-600 flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse" /> Live
                                </span>
                            </div>
                            <p className="text-[11px] text-stone-800 leading-snug">
                                You're <span className="font-bold text-amber-900">28g short on protein</span> today. Add 150g grilled paneer to dinner.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function PillarTile({ icon: Icon, label, value, tone }) {
    const tones = {
        red:    { bg: "bg-red-50",    text: "text-red-500",    label: "text-red-700" },
        indigo: { bg: "bg-indigo-50", text: "text-indigo-600", label: "text-indigo-700" },
        cyan:   { bg: "bg-cyan-50",   text: "text-cyan-600",   label: "text-cyan-700" },
    };
    const t = tones[tone];
    return (
        <div className={`${t.bg} rounded-lg px-2 py-1.5 flex items-center gap-1.5`}>
            <Icon className={`w-3 h-3 ${t.text} flex-shrink-0`} strokeWidth={2} />
            <div className="min-w-0">
                <p className={`${t.label} font-medium leading-tight`}>{label}</p>
                <p className="font-heading text-[11px] text-stone-900 leading-tight">{value}</p>
            </div>
        </div>
    );
}

function MealCard({ label, time, kcal, name, img, fallback, logged, recommended }) {
    return (
        <div
            className={`relative bg-stone-50 rounded-lg overflow-hidden border ${recommended ? "border-teal-400 animate-teal-border-glow" : "border-stone-200"}`}
            data-testid={`hero-meal-${label.toLowerCase()}`}
        >
            <div className="aspect-square bg-gradient-to-br from-amber-100 to-orange-100 relative flex items-center justify-center text-2xl">
                <span aria-hidden="true">{fallback}</span>
                <img
                    src={img}
                    alt={name}
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    loading="lazy"
                />
                {logged && (
                    <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-teal-500 text-white flex items-center justify-center text-[8px] font-bold z-10">✓</span>
                )}
                {recommended && (
                    <span className="absolute top-1 right-1 px-1.5 py-0.5 rounded-full bg-teal-500 text-white text-[7px] font-bold uppercase tracking-wide z-10">+ AI</span>
                )}
            </div>
            <div className="px-1.5 py-1">
                <p className="text-[8px] uppercase tracking-wider text-stone-500 font-bold leading-tight">{label}</p>
                <p className="text-[9px] text-stone-700 leading-tight truncate font-medium">{name}</p>
                <p className="text-[8px] text-stone-400 leading-tight">{time} · {kcal}</p>
            </div>
        </div>
    );
}

function MacroBar({ label, pct, color, value }) {
    return (
        <div>
            <div className="flex justify-between mb-0.5">
                <span className="text-stone-700">{label}</span>
                <span className="text-stone-500">{value}</span>
            </div>
            <div className="h-1 bg-stone-200 rounded-full overflow-hidden">
                <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}


// ============================================================================
// MOCKUPS for the new home sections — built in JSX (no asset bloat) to match
// the existing DashboardMockup convention. Each tells a single tight visual
// story; designed to be readable in a glance.
// ============================================================================

// Interactive condition picker — replaces the static onboarding screenshot.
// Click a card → reveal panel updates with that condition's tracked-signal tags
// and a CTA to the dedicated landing page (/pcos, /diabetes, etc.).
const CONDITIONS = [
    {
        slug: "pcos",
        name: "PCOS",
        tag: "Hormones + iron gaps",
        Icon: CircleDot,
        panel: {
            title: "PCOS — what your plan tracks",
            tags: ["Low-GI Indian meals", "Iron + Vit-C pairing", "Anti-inflammatory", "B12 + Vit-D gaps", "Insulin response"],
            cta: "See your PCOS plan — free trial",
        },
    },
    {
        slug: "diabetes",
        name: "Diabetes",
        tag: "Blood sugar control",
        Icon: Droplet,
        panel: {
            title: "Diabetes — what your plan tracks",
            tags: ["Glycaemic load per dish", "No hidden sugar", "Low-GI millets", "HbA1c-aware meals", "Fibre + protein balance"],
            cta: "See your diabetes plan — free trial",
        },
    },
    {
        slug: "thyroid",
        name: "Thyroid",
        tag: "Selenium + iodine",
        Icon: Activity,
        panel: {
            title: "Thyroid — what your plan tracks",
            tags: ["Selenium-rich dishes", "Iodine targets", "Goitrogen awareness", "Crucifer timing", "T3/T4 support foods"],
            cta: "See your thyroid plan — free trial",
        },
    },
    {
        slug: "hypertension",
        name: "Hypertension",
        tag: "Sodium-aware meals",
        Icon: HeartPulse,
        panel: {
            title: "Hypertension — what your plan tracks",
            tags: ["Sodium-aware meals", "Potassium targets", "DASH-aligned", "No processed foods", "Magnesium-rich dishes"],
            cta: "See your hypertension plan — free trial",
        },
    },
    {
        slug: "weight-loss",
        name: "Weight Management",
        tag: "Real Indian food",
        Icon: Scale,
        panel: {
            title: "Weight management — what your plan tracks",
            tags: ["Personalised calorie targets", "Indian macro ratios", "Satiety-first meals", "No Western templates", "Real kitchen portions"],
            cta: "See your weight loss plan — free trial",
        },
    },
    {
        slug: "post-pregnancy",
        name: "Post-Pregnancy",
        tag: "Recovery + iron",
        Icon: Baby,
        panel: {
            title: "Post-pregnancy — what your plan tracks",
            tags: ["Iron recovery", "Calcium for bones", "B12 for breastfeeding", "Anti-fatigue meals", "Gentle reintroduction"],
            cta: "See your post-pregnancy plan — free trial",
        },
    },
];

function ConditionPicker() {
    const [activeSlug, setActiveSlug] = useState("pcos");
    const active = CONDITIONS.find((c) => c.slug === activeSlug) || CONDITIONS[0];

    return (
        <div className="bg-white rounded-3xl shadow-2xl shadow-stone-900/10 border border-stone-200 p-6 md:p-7" data-testid="condition-picker">
            <div className="mb-5">
                <p className="text-[15px] font-semibold text-stone-900 leading-tight">What are you managing?</p>
                <p className="text-[13px] text-stone-500 mt-0.5">Select your condition — see what the plan does for it</p>
            </div>

            {/* Card grid: 3×2 desktop, 2×3 mobile */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 mb-5">
                {CONDITIONS.map((c) => {
                    const selected = c.slug === activeSlug;
                    const Icon = c.Icon;
                    return (
                        <button
                            key={c.slug}
                            type="button"
                            onClick={() => setActiveSlug(c.slug)}
                            data-testid={`condition-card-${c.slug}`}
                            className="text-left rounded-lg p-3 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
                            style={{
                                borderWidth: "0.5px",
                                borderStyle: "solid",
                                borderColor: selected ? "#0F6E56" : "#DDDDDA",
                                backgroundColor: selected ? "#E1F5EE" : "#FFFFFF",
                            }}
                            onMouseEnter={(e) => { if (!selected) e.currentTarget.style.borderColor = "#0F6E56"; }}
                            onMouseLeave={(e) => { if (!selected) e.currentTarget.style.borderColor = "#DDDDDA"; }}
                        >
                            <Icon className="w-4 h-4 mb-2" style={{ color: selected ? "#0F6E56" : "#888780" }} />
                            <p className="text-[13px] md:text-sm font-bold text-stone-900 leading-tight">{c.name}</p>
                            <p className="text-[11px] text-stone-500 mt-0.5">{c.tag}</p>
                        </button>
                    );
                })}
            </div>

            {/* Reveal panel — fixed min-height so switching conditions never
                reflows the cards above. Picks the tallest condition (longest
                pill labels wrapping to 3 lines on narrow widths) as the floor. */}
            <div
                data-testid="condition-reveal-panel"
                className="rounded-lg px-3.5 py-3"
                style={{
                    backgroundColor: "#F0FBF7",
                    borderLeft: "3px solid #0F6E56",
                    minHeight: "150px",
                }}
            >
                <p className="text-xs font-semibold mb-2" style={{ color: "#085041" }}>{active.panel.title}</p>
                <div className="flex flex-wrap gap-1.5 mb-3">
                    {active.panel.tags.map((t) => (
                        <span
                            key={t}
                            className="text-[10px] font-medium px-2 py-1 rounded-full"
                            style={{ backgroundColor: "#E1F5EE", color: "#085041" }}
                        >
                            {t}
                        </span>
                    ))}
                </div>
                <Link
                    to={`/${active.slug}`}
                    data-testid={`condition-cta-${active.slug}`}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold hover:gap-2 transition-all"
                    style={{ color: "#0F6E56" }}
                >
                    {active.panel.cta}
                    <ArrowRight className="w-3 h-3" />
                </Link>
            </div>
        </div>
    );
}


function DeficiencyReasonCard() {
    return (
        <div className="bg-white border border-stone-200 rounded-3xl p-7 md:p-8 hover:-translate-y-1 transition-transform" data-testid="reason-card-blood">
            <div className="flex items-start justify-between mb-6">
                <div>
                    <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-rose-600 mb-2">BECAUSE OF YOUR BLOOD REPORT</p>
                    <h3 className="font-heading text-2xl text-stone-900 leading-tight">Iron, B12 and Vitamin D — flagged.</h3>
                </div>
                <div className="h-11 w-11 rounded-xl bg-rose-50 flex items-center justify-center shrink-0">
                    <FlaskConical className="w-5 h-5 text-rose-600" />
                </div>
            </div>

            {/* Deficiency rows */}
            <div className="space-y-3 mb-6">
                {[
                    { name: "Iron (Ferritin)", value: "12 ng/mL", target: "30-150", icon: Droplet, iconBg: "bg-rose-100 text-rose-600" },
                    { name: "Vitamin B12", value: "180 pg/mL", target: "300-900", icon: Apple, iconBg: "bg-amber-100 text-amber-600" },
                    { name: "Vitamin D", value: "18 ng/mL", target: "30-100", icon: Sun, iconBg: "bg-amber-100 text-amber-600" },
                ].map((d) => (
                    <div key={d.name} className="flex items-center gap-3 py-2.5 px-3.5 rounded-xl bg-stone-50 border border-stone-100">
                        <div className={`h-8 w-8 rounded-lg ${d.iconBg} flex items-center justify-center shrink-0`}>
                            <d.icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-stone-900 truncate">{d.name}</p>
                            <p className="text-[11px] text-stone-500">Ref: {d.target}</p>
                        </div>
                        <div className="text-right">
                            <p className="text-sm font-semibold tabular-nums text-rose-600">{d.value}</p>
                            <p className="text-[10px] uppercase font-semibold tracking-wider text-rose-500">Low</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Recommendation */}
            <div className="bg-teal-50 border border-teal-200 rounded-2xl p-4 flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />
                <div>
                    <p className="text-sm font-semibold text-stone-900 mb-1">Your week tilts toward iron + B12.</p>
                    <p className="text-xs text-stone-700 leading-relaxed">
                        Spinach dal Monday. Curd with sprouted moong Tuesday. Egg bhurji + tomato Wednesday. Every meal hits a deficiency you actually have.
                    </p>
                </div>
            </div>
        </div>
    );
}


function CourseCorrectReasonCard() {
    return (
        <div className="bg-white border border-stone-200 rounded-3xl p-7 md:p-8 hover:-translate-y-1 transition-transform" data-testid="reason-card-day">
            <div className="flex items-start justify-between mb-6">
                <div>
                    <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-teal-600 mb-2">BECAUSE OF YOUR DAY SO FAR</p>
                    <h3 className="font-heading text-2xl text-stone-900 leading-tight">It&rsquo;s 4 PM. You&rsquo;re 28g short on protein.</h3>
                </div>
                <div className="h-11 w-11 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                    <Zap className="w-5 h-5 text-teal-600" />
                </div>
            </div>

            {/* Today macro snapshot */}
            <div className="grid grid-cols-4 gap-2 mb-5">
                {[
                    { label: "Protein", value: "68", target: "96", pct: 71, barCls: "bg-rose-500" },
                    { label: "Carbs", value: "180", target: "210", pct: 86, barCls: "bg-teal-500" },
                    { label: "Fat", value: "44", target: "58", pct: 76, barCls: "bg-teal-500" },
                    { label: "Kcal", value: "1480", target: "1750", pct: 85, barCls: "bg-teal-500" },
                ].map((m) => (
                    <div key={m.label} className="bg-stone-50 border border-stone-100 rounded-xl px-2.5 py-2.5 text-center">
                        <p className="text-[9px] uppercase font-bold tracking-wider text-stone-500 mb-1">{m.label}</p>
                        <p className="text-base font-semibold text-stone-900 tabular-nums leading-tight">{m.value}<span className="text-[10px] text-stone-400">/{m.target}</span></p>
                        <div className="h-1 bg-stone-200 rounded-full overflow-hidden mt-1.5">
                            <div className={`h-full ${m.barCls}`} style={{ width: `${m.pct}%` }} />
                        </div>
                    </div>
                ))}
            </div>

            {/* The reasoning step-by-step */}
            <div className="space-y-2.5 mb-5">
                {[
                    { num: 1, label: "Meal logged.", body: "Lunch · roti + dal + sabzi · 320 kcal · 12g protein" },
                    { num: 2, label: "Gap detected.", body: "Protein at 71% of target. Window closing." },
                    { num: 3, label: "Recommendation generated.", body: "Add 150g grilled paneer at dinner. +27g protein." },
                ].map((s) => (
                    <div key={s.num} className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-full bg-stone-900 text-white text-[11px] font-semibold flex items-center justify-center shrink-0 tabular-nums">{s.num}</div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-stone-900">{s.label}</p>
                            <p className="text-xs text-stone-600 leading-snug">{s.body}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Course-correct CTA */}
            <div className="bg-stone-900 text-white rounded-2xl p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-teal-400 flex items-center justify-center shrink-0">
                    <Beef className="w-5 h-5 text-stone-900" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold leading-tight">Add to dinner — grilled paneer</p>
                    <p className="text-[11px] text-stone-400 leading-tight">150g · 27g protein · 330 kcal</p>
                </div>
                <button className="text-xs font-semibold bg-teal-400 text-stone-900 rounded-full px-3.5 py-2 shrink-0 hover:bg-teal-300 transition-colors">+ Log</button>
            </div>
        </div>
    );
}


function TargetsMockup() {
    return (
        <div className="relative">
            {/* Main targets card — dark gradient mirrors the in-app Targets screen */}
            <div className="bg-gradient-to-br from-stone-900 via-stone-900 to-stone-800 rounded-3xl shadow-2xl shadow-stone-900/20 p-7 md:p-9 text-white relative overflow-hidden" data-testid="targets-mockup">
                {/* Soft glow */}
                <div className="absolute inset-0 opacity-50" style={{ backgroundImage: "radial-gradient(circle at 80% 0%, rgba(45,212,191,.15), transparent 50%)" }} />

                <div className="relative">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-teal-300 mb-1">YOUR DAILY TARGET</p>
                            <p className="text-sm text-stone-400">Calculated from your stats + goal</p>
                        </div>
                        <div className="h-10 w-10 rounded-xl bg-teal-400/10 border border-teal-400/20 flex items-center justify-center">
                            <Target className="w-4 h-4 text-teal-300" />
                        </div>
                    </div>

                    {/* Big calorie number */}
                    <div className="mb-7">
                        <div className="flex items-baseline gap-2">
                            <span className="font-heading text-6xl md:text-7xl tracking-tight tabular-nums">1,710</span>
                            <span className="text-base text-stone-400 font-medium">kcal/day</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-3">
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-rose-500/15 text-rose-300 px-2.5 py-1 rounded-full border border-rose-500/20">
                                <TrendingUp className="w-3 h-3 rotate-180" /> −428 kcal deficit
                            </span>
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-teal-500/15 text-teal-300 px-2.5 py-1 rounded-full border border-teal-500/20">
                                <Calendar className="w-3 h-3" /> ~26 weeks to goal
                            </span>
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-amber-500/15 text-amber-200 px-2.5 py-1 rounded-full border border-amber-500/20">
                                Safe pace
                            </span>
                        </div>
                    </div>

                    {/* Macro tiles */}
                    <div className="grid grid-cols-3 gap-2.5 mb-5">
                        {[
                            { label: "Protein", value: "150", unit: "g", per: "1.6 g/kg body weight", colorBar: "bg-rose-400" },
                            { label: "Carbs", value: "171", unit: "g", per: "40% of kcal", colorBar: "bg-amber-300" },
                            { label: "Fat", value: "48", unit: "g", per: "25% of kcal", colorBar: "bg-teal-300" },
                        ].map((m) => (
                            <div key={m.label} className="bg-white/5 border border-white/10 rounded-xl p-3.5">
                                <p className="text-[10px] uppercase font-bold tracking-wider text-stone-400 mb-1">{m.label}</p>
                                <p className="font-heading text-2xl tabular-nums text-white leading-tight">{m.value}<span className="text-xs text-stone-400 font-normal">{m.unit}</span></p>
                                <p className="text-[10px] text-stone-500 mt-0.5">{m.per}</p>
                                <div className={`h-0.5 ${m.colorBar} rounded-full mt-2.5 opacity-80`} />
                            </div>
                        ))}
                    </div>

                    {/* Recalc note */}
                    <div className="flex items-center gap-2.5 pt-4 border-t border-white/10">
                        <Sparkles className="w-4 h-4 text-teal-300 shrink-0" />
                        <p className="text-xs text-stone-300">
                            Recalculated every week as your weight, activity and signals change.
                        </p>
                    </div>
                </div>
            </div>

            {/* Floating: starting → goal */}
            <div className="hidden md:flex absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl border border-stone-200 p-3.5 items-center gap-3 z-10">
                <div className="h-9 w-9 rounded-full bg-emerald-50 flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-emerald-600 rotate-180" />
                </div>
                <div>
                    <p className="text-[10px] text-stone-500 leading-tight">From → to</p>
                    <p className="text-xs font-semibold text-stone-900 leading-tight tabular-nums">82 → 66 kg</p>
                </div>
            </div>

            {/* Floating: BMR */}
            <div className="hidden md:flex absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl border border-stone-200 p-3.5 items-center gap-3 z-10">
                <div className="h-9 w-9 rounded-full bg-indigo-50 flex items-center justify-center">
                    <Calculator className="w-4 h-4 text-indigo-600" />
                </div>
                <div>
                    <p className="text-[10px] text-stone-500 leading-tight">BMR · TDEE</p>
                    <p className="text-xs font-semibold text-stone-900 leading-tight tabular-nums">1,432 · 2,138</p>
                </div>
            </div>
        </div>
    );
}

